'use strict';

Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.injectReducers = injectReducers;
exports.createApp = createApp;
exports.createModule = createModule;
exports.createState = createState;
exports.mockReset = mockReset;
exports.mockModule = mockModule;

var _angular = require('angular');

var _angular2 = _interopRequireDefault(_angular);

var _redux = require('redux');

var _reduxThunk = require('redux-thunk');

var _reduxThunk2 = _interopRequireDefault(_reduxThunk);

var _lodash = require('lodash.assignin');

var _lodash2 = _interopRequireDefault(_lodash);

var _lodash3 = require('lodash.isempty');

var _lodash4 = _interopRequireDefault(_lodash3);

var _ngRedux = require('ng-redux');

var _ngRedux2 = _interopRequireDefault(_ngRedux);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function processDeps(dependencies) {
    var processedDeps = {};
    processedDeps.angular = dependencies.map(function (dep) {
        if (typeof dep !== 'string') {
            return dep.name;
        }

        return dep;
    });
    processedDeps.reducer = dependencies.reduce(function (current, next) {
        return next.reducer ? (0, _lodash2.default)({}, current, next.reducer) : current;
    }, {});

    return processedDeps;
}

function AppStateService($ngRedux) {
    var service = {};

    service.connect = function connect(scope, selector, mapper) {
        var unsubscribe = $ngRedux.connect(selector)(mapper);
        scope.$on('$destroy', unsubscribe);
        return unsubscribe; //Return ref to allow "disconnect" manually
    };

    return service;
}

var reduxSymbol = Symbol('root redux store');
window[reduxSymbol] = {
    rootStore: undefined,
    fullReducerMap: {}
};

/**
 * Dynamically adds additional reducers to running redux store
 * ref: https://redux.js.org/recipes/code-splitting
 * 
 * @param {Object.<string, function>} reducers 
 * @throws if no redux store exists
 */
function injectReducers(reducers) {
    var _window$reduxSymbol = window[reduxSymbol],
        rootStore = _window$reduxSymbol.rootStore,
        fullReducerMap = _window$reduxSymbol.fullReducerMap;


    if (!rootStore) {
        throw new Error('No redux store exists.');
    }
    (0, _lodash2.default)(fullReducerMap, reducers);
    rootStore.replaceReducer((0, _redux.combineReducers)(fullReducerMap));
}

/**
 * @method createApp
 * @description 
 * creates an angular module with a root redux store by combining reducers from all dependent modules 
 * with the appReducer provided to this method.
 *
 * @export
 * @param {*} name
 * @param {Array.<angular.module>} deps
 * @param {*} appReducer
 * @returns
 */
function createApp(name, deps, appReducer) {
    var processedDeps = processDeps(deps.concat(_ngRedux2.default));

    return _angular2.default.module(name, processedDeps.angular).config(['$ngReduxProvider', function reduxConfig($ngReduxProvider) {
        var reducerMap = (0, _lodash2.default)(processedDeps.reducer, appReducer);
        var _window$reduxSymbol2 = window[reduxSymbol],
            rootStore = _window$reduxSymbol2.rootStore,
            fullReducerMap = _window$reduxSymbol2.fullReducerMap;


        if (rootStore) {
            injectReducers(reducerMap);
            $ngReduxProvider.provideStore(rootStore);
            return;
        }

        var storeEnhancers = [];
        var reducer = void 0;

        if ((0, _lodash4.default)(reducerMap)) {
            reducer = function statePassthrough(state) {
                return state || {};
            };
        } else {
            reducer = (0, _redux.combineReducers)(reducerMap);
        }

        if (process.env.NODE_ENV !== 'production' && window.devToolsExtension) {
            storeEnhancers.push(window.devToolsExtension());
        }

        (0, _lodash2.default)(fullReducerMap, reducerMap);
        $ngReduxProvider.createStoreWith(reducer, [_reduxThunk2.default], storeEnhancers);
    }]).run(['$ngRedux', '$rootScope', function devToolsRun($ngRedux, $rootScope) {
        var rootStore = window[reduxSymbol].rootStore;


        if (rootStore) {
            return;
        }
        window[reduxSymbol].rootStore = $ngRedux;

        //We need to listen to state changes outside of the Redux flow when using dev tools
        if (process.env.NODE_ENV !== 'production' && window.devToolsExtension) {
            $ngRedux.subscribe(function () {
                setTimeout(function () {
                    $rootScope.$apply();
                }, 100);
            });
        }
    }]).service('AppState', ['$ngRedux', AppStateService]);
}

/**
 * @method createModule
 * @description
 * creates an angular module with a reducer that is the combination of 
 * all the reducers of dependent modules and an optional reducer provided to
 * this method. this module can then be consumed by one created by `createApp`
 *
 * @export
 * @param {String} name
 * @param {Array.<angular.module>} deps
 * @param {*} [reducer={}]
 * @returns
 */
function createModule(name, deps) {
    var reducer = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    var processedDeps = processDeps(deps);
    var localModule = _angular2.default.module(name, processedDeps.angular).service('AppState', ['$ngRedux', AppStateService]);
    localModule.reducer = (0, _lodash2.default)(reducer, processedDeps.reducer);
    return localModule;
}

function createState(initialState, reducers) {
    return function reduce(state, action) {
        var reducer = reducers[action.type];
        if (reducer) {
            return reducer(state, action);
        }
        return state || initialState;
    };
}

/**
 * @method mockReset
 * @description
 * Use in unit test context to reset the stored root redux store
 */
function mockReset() {
    window[reduxSymbol].rootStore = undefined;
    window[reduxSymbol].fullReducerMap = {};
}

/**
 * @method mockModule
 * @description
 * Use in unit test context to mock a module that should use redux (i.e. created with `createModule`)
 * note: modules created with `createApp` shouldn't require this kind of mocking
 * 
 * @param {angular.module} testModule 
 * @param {Function} [initializeFunction] 
 */
function mockModule(testModule, initializeFunction) {
    if (typeof testModule === 'function') {
        // In this case, a user is using angular.mock.module to modify something in 
        // the AngularJS DI registry via an injection function
        return _angular2.default.mock.module(testModule);
    }

    if (typeof testModule === 'string') {
        // eslint-disable-next-line no-param-reassign
        testModule = _angular2.default.module(testModule);
    }

    var mockModuleName = 'test.' + testModule.name;
    createApp(mockModuleName, [testModule], {});

    return typeof initializeFunction === 'function' ? _angular2.default.mock.module(mockModuleName, initializeFunction) : _angular2.default.mock.module(mockModuleName);
}

exports.default = {
    createApp: createApp, createModule: createModule, createState: createState
};